<template>
  <v-dialog v-model="visible" max-width="760" persistent>
    <v-card rounded="lg">
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="imp__title">
          <ClipboardPaste :size="18" />
          {{ $gt(title) }}
        </span>
        <ButtonBase color="grey" icon-style icon-type="cancel" @click="close" />
      </v-card-title>

      <v-card-text class="pa-4 pt-0">
        <p class="imp__lead">
          {{ $gt(LEAD_TEXT) }}
        </p>

        <textarea
          v-model="raw"
          class="imp__textarea"
          :placeholder="$gt('Text oder HTML hier einfügen…')"
          rows="10"
        />

        <div class="imp__row">
          <label class="imp__file">
            <Upload :size="14" />
            <span>{{ $gt('Datei wählen (.txt, .html)') }}</span>
            <input accept=".txt,.html,.htm,text/plain,text/html" class="d-none" type="file" @change="onFile">
          </label>
          <span v-if="fileName" class="imp__filename">{{ fileName }}</span>
          <span v-if="raw" class="imp__kind">{{ isHtml ? $gt('erkannt: HTML') : $gt('erkannt: Text') }}</span>
        </div>

        <div v-if="previewHtml" class="imp__preview">
          <span class="imp__preview-label">{{ $gt('Vorschau') }}</span>
          <div class="imp__preview-body" v-html="previewHtml" />
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions class="d-flex justify-end ma-2">
        <ButtonBase button-text="Abbrechen" icon-type="cancel" @click="close" />
        <ButtonBase
          :button-text="confirmLabel"
          :disabled="!previewHtml"
          icon-type="check"
          variant="flat"
          @click="confirm"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
  import { ClipboardPaste, Upload } from 'lucide-vue-next'
  import { computed, ref, watch } from 'vue'
  import ButtonBase from '@/components/buttons/ButtonBase.vue'
  import { importedContentToHtml, looksLikeHtml } from '@/utils/mailTemplate'

  /**
   * "Aus Text/HTML einfügen": nimmt eingefügten Text oder eine Datei
   * entgegen, wandelt sie in Editor-HTML und liefert das Ergebnis über das
   * `import`-Event. Genutzt vom Vorlagen-Editor und vom Signatur-Editor.
   */
  const props = defineProps<{
    modelValue: boolean
    title?: string
    confirmLabel?: string
  }>()

  const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void
    (e: 'import', html: string): void
  }>()

  // Als Konstante, weil der Text selbst Platzhalter-Klammern enthält, die
  // Vue im Template als Interpolation lesen würde.
  const LEAD_TEXT = 'Kopieren Sie den Text Ihrer bestehenden Vorlage (z. B. aus Word oder Outlook) in das Feld oder laden Sie eine .txt- bzw. .html-Datei hoch. Zeilenumbrüche und einfache Formatierungen bleiben erhalten; Platzhalter wie {{vorname}} können Sie danach über die Chips ergänzen.'

  const title = computed(() => props.title || 'Aus Text/HTML einfügen')
  const confirmLabel = computed(() => props.confirmLabel || 'Übernehmen')

  const visible = computed({
    get: () => props.modelValue,
    set: value => emit('update:modelValue', value),
  })

  const raw = ref('')
  const fileName = ref('')

  const isHtml = computed(() => looksLikeHtml(raw.value))
  const previewHtml = computed(() => importedContentToHtml(raw.value))

  watch(() => props.modelValue, open => {
    if (open) {
      raw.value = ''
      fileName.value = ''
    }
  })

  async function onFile (event: Event) {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    input.value = ''
    if (!file) return
    fileName.value = file.name
    try {
      raw.value = await file.text()
    } catch {
      raw.value = ''
    }
  }

  function close () {
    visible.value = false
  }

  function confirm () {
    if (!previewHtml.value) return
    emit('import', previewHtml.value)
    visible.value = false
  }
</script>

<style scoped lang="scss">
.imp__title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;

  svg {
    color: var(--primary-color, #0076e5);
  }
}

.imp__lead {
  margin: 0 0 12px;
  font-size: 0.82rem;
  line-height: 1.5;
  color: var(--grey-color, #5f5f5f);
}

.imp__textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--border-grey-color, #d0d5dd);
  border-radius: 10px;
  font: inherit;
  font-size: 0.85rem;
  line-height: 1.5;
  resize: vertical;
  outline: none;

  &:focus {
    border-color: var(--primary-color, #0076e5);
    box-shadow: 0 0 0 3px rgba(0, 118, 229, 0.12);
  }
}

.imp__row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.imp__file {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px dashed var(--border-grey-color, #d0d5dd);
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--dark-color, #323232);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: var(--primary-color, #0076e5);
    background: var(--primary-light-color, #deeefb);
  }
}

.imp__filename,
.imp__kind {
  font-size: 0.76rem;
  color: var(--grey-color, #5f5f5f);
}

.imp__kind {
  margin-left: auto;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--grey-light-color, #f3f5f7);
}

.imp__preview {
  margin-top: 14px;
  border: 1px solid var(--border-grey-color-light, #e6eaf0);
  border-radius: 10px;
  overflow: hidden;
}

.imp__preview-label {
  display: block;
  padding: 6px 12px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--grey-color, #5f5f5f);
  background: var(--grey-light-color, #f3f5f7);
}

.imp__preview-body {
  max-height: 220px;
  overflow-y: auto;
  padding: 12px 14px;
  font-size: 0.84rem;
  line-height: 1.55;
}
</style>
